import { loadPdfMake } from '../utils/loadPdfMake'
import { formatBusinessDate as format } from '../utils/businessTime'
import { resolvePdfGraphic } from './pdfMedia'


const money = value => `$${Number(value || 0).toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
})}`
const quoteDate = value => format(new Date(value), 'dd MMM yyyy').replace(/\bSep\b/, 'Sept')
const quoteQuantity = item => {
    const quantity = Number(item.quantity || 0) * Number(item.duration ?? 1)
    const units = { Hours: ['hour', 'hours'], Days: ['day', 'days'], Weeks: ['week', 'weeks'], Unit: ['Unit', 'Units'] }
    const labels = units[item.durationUnit] || units.Hours
    return `${quantity.toLocaleString('en-AU', { maximumFractionDigits: 4 })} ${labels[quantity === 1 ? 0 : 1]}`
}

export const buildQuotePdfDocument = async (quote, logo) => {
    const logoGraphic = await resolvePdfGraphic(logo)
    const organisation = quote.company || {}
    const client = quote.client || {}
    const siteLocation = [quote.site?.name, quote.location || quote.site?.address].filter(Boolean).join(' - ')
    const rows = (quote.items || []).map(item => [
        { text: item.description || '', alignment: 'left' },
        { text: quoteQuantity(item), alignment: 'center' },
        { text: money(item.rate), alignment: 'right' },
        { text: money(item.gst), alignment: 'right' },
        { text: money(item.total), alignment: 'right' },
    ])

    return {
        pageSize: 'A4',
        pageMargins: [54, 60, 54, 54],
        content: [
            {
                columns: [
                    logoGraphic ? { ...logoGraphic, fit: [125, 48], width: 180 } : { text: organisation.name || '', bold: true, fontSize: 14, width: 180 },
                    { text: 'QUOTE', alignment: 'right', bold: true, fontSize: 20 },
                ],
                margin: [0, 0, 0, 24],
            },
            {
                columns: [
                    {
                        width: '*',
                        stack: [
                            { text: organisation.name || '', bold: true },
                            organisation.abn ? { text: `ABN: ${organisation.abn}` } : null,
                            organisation.address ? { text: organisation.address } : null,
                            organisation.phone ? { text: organisation.phone } : null,
                            organisation.email ? { text: organisation.email } : null,
                        ].filter(Boolean),
                    },
                    {
                        width: '*',
                        alignment: 'right',
                        stack: [
                            { text: [{ text: 'Prepared For: ', bold: true }, client.name || ''] },
                            client.abn ? { text: `ABN: ${client.abn}` } : null,
                            client.address ? { text: client.address } : null,
                        ].filter(Boolean),
                    },
                ],
                margin: [0, 0, 0, 18],
            },
            {
                columns: [
                    {
                        width: '*',
                        stack: [
                            siteLocation ? { text: [{ text: 'Site / Location: ', bold: true }, siteLocation] } : null,
                            quote.reference ? { text: [{ text: 'Reference: ', bold: true }, quote.reference] } : null,
                        ].filter(Boolean),
                    },
                    {
                        width: 190,
                        alignment: 'right',
                        stack: [
                            { text: [{ text: 'Quote: ', bold: true }, quote.quoteNumber || 'Draft'] },
                            { text: [{ text: 'Date: ', bold: true }, quoteDate(quote.quoteDate)] },
                            { text: [{ text: 'Valid Until: ', bold: true }, quoteDate(quote.validUntil)] },
                        ],
                    },
                ],
                margin: [0, 0, 0, 18],
            },
            {
                table: {
                    headerRows: 1,
                    widths: ['*', 72, 55, 55, 64],
                    body: [
                        [
                            { text: 'Description', bold: true },
                            { text: 'Qty', bold: true, alignment: 'center' },
                            { text: 'Rate', bold: true, alignment: 'right' },
                            { text: 'GST', bold: true, alignment: 'right' },
                            { text: 'Total', bold: true, alignment: 'right' },
                        ],
                        ...rows,
                    ],
                },
                layout: 'lightHorizontalLines',
            },
            {
                columns: [
                    { width: '*', text: '' },
                    {
                        width: 190,
                        table: {
                            widths: ['*', 78],
                            body: [
                                [{ text: 'Subtotal', alignment: 'right' }, { text: money(quote.subtotal), alignment: 'right' }],
                                [{ text: 'GST', alignment: 'right' }, { text: money(quote.gst), alignment: 'right' }],
                                [{ text: 'Total', bold: true, alignment: 'right' }, { text: money(quote.total), bold: true, alignment: 'right' }],
                            ],
                        },
                        layout: 'noBorders',
                    },
                ],
                margin: [0, 12, 0, 24],
            },
            quote.notes ? { text: 'Notes', bold: true, margin: [0, 0, 0, 5] } : null,
            quote.notes ? { text: quote.notes } : null,
        ].filter(Boolean),
        defaultStyle: { fontSize: 10, color: '#172033' },
    }
}

const createPdf = async (quote, logo) => {
    const pdfMake = await loadPdfMake()
    return pdfMake.createPdf(await buildQuotePdfDocument(quote, logo))
}

export const downloadQuotePdf = async (quote, logo) => {
    const pdf = await createPdf(quote, logo)
    pdf.download(`quote_${quote.quoteNumber || 'draft'}.pdf`)
}

export const previewQuotePdf = async (quote, logo) => {
    // Reserve the tab during the click, before a cold engine/font download can
    // consume the browser's user-activation window.
    const previewWindow = window.open('', '_blank')
    try {
        const pdf = await createPdf(quote, logo)
        pdf.open({}, previewWindow)
    } catch (error) {
        previewWindow?.close()
        throw error
    }
}
